import { useTheme, Grid, Typography, DialogContent, Checkbox } from "@material-ui/core";
import { nanoid } from "@reduxjs/toolkit";
import React, { useState, useMemo, useEffect } from "react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { SimpleDialog, DropdownWithLabel, InputWithLabel, DraggableDialog, EnhancedTable, EnhancedTableCheckboxHead } from "../components";
import { Asset, updateWebMapLayer, WebMapData, WebMapLayerData } from "../redux/assets-slice";
import { ServerType } from "../types/web-sources";
import { useAssetTools } from "../utilities";
import {Parser as XMLParser} from 'xml2js';
import WMTSCapabilities from 'ol/format/WMTSCapabilities.js';
import WMSCapabilities from 'ol/format/WMSCapabilities.js';
import { useTableState } from "../hooks/use-table-state";
import { useDispatch } from "react-redux";

interface WebSourceDialogProps {
  open: boolean;
  onClose(): void;
  folderID: string;
}

export const WebSourceDialog = (props: WebSourceDialogProps) => {
  const {open, onClose, folderID} = props;

  const theme = useTheme();
  const {t} = useTranslation();
  const {addWebSource} = useAssetTools();

  const [serverURL, setServerURL] = useState("");
  const [serverName, setServerName] = useState("");
  const [serverType, setServerType] = useState(ServerType.ArcGISMapServer);
  const [submitting, setSubmitting] = useState(false);

  const onSubmit = async () => {
    setSubmitting(true);

    try {
      const webmapData = {
        url: serverURL,
        type: serverType,
        public: true,
        layers: await getMapLayers(serverURL, serverType)
      };

      await addWebSource(serverName, webmapData, folderID);

      setSubmitting(false);
      onClose();
    } catch {
      toast.error("Error grabbing layers from URL");
      setSubmitting(false);
    }
  };

  const getMapServerLayers = async (baseURL: string) => {
    const url = new URL(baseURL);
    url.searchParams.set('f', "json");
    url.searchParams.set('pretty', "true");

    const response = await fetch(url.href);
    const data = await response.json();
    const layers = data.layers as any[];

    return layers.map(layer => {
      return {
        id: nanoid(),
        visible: true,
        identifier: layer.id,
        name: layer.name,
      };
    });
  };

  const getFeatureServerLayers = async (baseURL: string) => {
    const url = new URL(baseURL);
    url.searchParams.set('f', "json");
    url.searchParams.set('pretty', "true");

    const response = await fetch(url.href);
    const data = await response.json();
    const layers = data.layers as any[];

    return layers.map(layer => {
      return {
        id: nanoid(),
        visible: true,
        identifier: layer.id,
        name: layer.name,
      };
    });
  };

  const getImageServerLayers = async (baseURL: string) => {
    const url = new URL(baseURL);
    url.searchParams.set('f', "json");
    url.searchParams.set('pretty', "true");

    const response = await fetch(url.href);
    if (!response.ok) {
      throw new Error("Error verifying image server URL");
    }

    // No layers for this server type
    return [];
  };

  const getWMSLayers = async (baseURL: string) => {
    const url = new URL(baseURL);
    url.searchParams.set('SERVICE', "WMS");
    url.searchParams.set('REQUEST', "GetCapabilities");

    const response = await fetch(url.href);
    const text = await response.text();

    const parser = new WMSCapabilities();
    const capabilities = parser.read(text);
    const layers = capabilities.Capability.Layer.Layer as any[];

    return layers.map(layer => {
      return {
        id: nanoid(),
        visible: true,
        identifier: layer.Name,
        name: layer.Title
      };
    });
  };

  const getWFSLayers = async (baseURL: string) => {
    const url = new URL(baseURL);
    url.searchParams.set('SERVICE', "WFS");
    url.searchParams.set('REQUEST', "GetCapabilities");

    const response = await fetch(url.href);
    const text = await response.text();

    const parser = new XMLParser({
      explicitChildren:true,
      preserveChildrenOrder: true
    });

    const xmlJSON = await parser.parseStringPromise(text);
    const capabilities = xmlJSON['wfs:WFS_Capabilities'];
    const featureTypeList = capabilities.FeatureTypeList[0];
    const features = featureTypeList.FeatureType as any[];

    return features.map(layer => {
      return {
        id: nanoid(),
        visible: true,
        identifier: layer.Name[0],
        name: layer.Title[0]
      };
    });
  };

  const getWMTSLayers = async (baseURL: string) => {
    const url = new URL(baseURL);
    url.pathname = `${url.pathname}/1.0.0/WMTSCapabilities.xml`;

    const response = await fetch(url.href);
    const text = await response.text();

    const parser = new WMTSCapabilities();
    const capabilities = parser.read(text);
    const layers = capabilities.Contents.Layer as any[];

    return layers.map(layer => {
      return {
        id: nanoid(),
        visible: true,
        identifier: layer.Identifier,
        name: layer.Title
      };
    });
  };

  const getTileServerLayers = async (baseURL: string) => {
    // No layers for this server type
    return [];
  };

  const getMapLayers = (url, type) : Promise<WebMapLayerData[]> => {
    switch (type) {
    case ServerType.ArcGISMapServer:
      return getMapServerLayers(url);
    case ServerType.ArcGISImageServer:
      return getImageServerLayers(url);
    case ServerType.ArcGISFeatureServer:
      return getFeatureServerLayers(url);
    case ServerType.OGCWMS:
      return getWMSLayers(url);
    case ServerType.OGCWFS:
      return getWFSLayers(url);
    case ServerType.OGCWMTS:
      return getWMTSLayers(url);
    case ServerType.TileServer:
      return getTileServerLayers(url);
    default:
      throw new Error("Error grabbing map layers");
    }
  };

  const resetDefaults = () => {
    setServerURL("");
    setServerName("");
    setSubmitting(false);
    setServerType(ServerType.ArcGISMapServer);
  };

  const isSecureURL = useMemo(() => {
    try {
      const url = new URL(serverURL);
      return url.protocol === "https:";
    } catch {
      return false;
    }
  }, [serverURL]);

  /** Check url to make sure it matches the required syntax */
  const isValidURL = useMemo(() => {
    const isArcGISServer = (serverType === ServerType.ArcGISMapServer)
      || (serverType === ServerType.ArcGISImageServer)
      || (serverType === ServerType.ArcGISFeatureServer);

    if (isArcGISServer) {
      try {
        const testURL = new URL(serverURL);
        const mapType = testURL.pathname
          .split("/")
          .pop()
          .toLowerCase();

        if (serverType === ServerType.ArcGISMapServer) {
          return mapType === "mapserver";
        } else if (serverType === ServerType.ArcGISImageServer) {
          return mapType === "imageserver";
        } else if (serverType === ServerType.ArcGISFeatureServer) {
          return mapType === "featureserver";
        }
      } catch {
        return false;
      }
    }

    if (serverType === ServerType.TileServer) {
      const hasX = serverURL.includes("{x}")
        || serverURL.includes("{col}");

      const hasY = serverURL.includes("{y}")
        || serverURL.includes("{-y}")
        || serverURL.includes("{row}");

      const hasZ = serverURL.includes("{z}")
        || serverURL.includes("{level}")
        || serverURL.includes("{zoom}");

      return hasX && hasY && hasZ;
    }

    return true;
  }, [serverURL, serverType]);

  const placeholder = useMemo(() => {
    switch (serverType) {
    case ServerType.ArcGISMapServer:
      return "https://server.arcgisonline.com/..../MapServer";
    case ServerType.ArcGISFeatureServer:
      return "https://server.arcgisonline.com/..../FeatureServer/";
    case ServerType.ArcGISImageServer:
      return "https://server.arcgisonline.com/..../ImageServer/";
    case ServerType.ArcGISOnline:
      return "https://arcgis.com/home/webmap/viewer.html?webmap=MAPID";
    case ServerType.OGCWMS:
      return "https://example.server.com/wms";
    case ServerType.OGCWMTS:
      return "https://example.server.com/wmts";
    case ServerType.OGCWFS:
      return "https://example.server.com/wfs";
    case ServerType.TileServer:
      return "https://custom.server.com/tiles/{z}/{x}/{y}.png";
    default:
      return;
    }
  }, [serverType]);

  const label = useMemo(() => {
    const warnings = [];

    if (!isSecureURL) {
      warnings.push(t('websources.only_secure_urls_https_are_supported'));
    }

    if (!isValidURL) {
      warnings.push(t('websources.invalid_url_for_this_server_type'));
    }

    return warnings.join(" ");
  }, [isSecureURL, isValidURL]);

  useEffect(() => {
    if (!open) return;
    resetDefaults();
  }, [open]);

  const showEsriWarning = (serverType === ServerType.ArcGISFeatureServer)
    || (serverType === ServerType.ArcGISImageServer)
    || (serverType === ServerType.ArcGISMapServer)
    || (serverType === ServerType.ArcGISOnline);

  const error = !isSecureURL
    || !isValidURL;

  const canSubmit = isSecureURL
    && isValidURL
    && !!serverName
    && !submitting;

  return (
    <SimpleDialog
      maxWidth={"xs"}
      title={t('websources.add_web_source')}
      open={open}
      canSubmit={canSubmit}
      canClose={!submitting}
      onSubmit={onSubmit}
      onClose={onClose}
    >
      <Grid container spacing={2}>
        {/** Icon Size */}
        <Grid item xs={12}>
          <DropdownWithLabel
            title={t('websources.server_type')}
            value={serverType}
            onChange={setServerType}
            disabled={submitting}
            options={[
              [ServerType.ArcGISMapServer, "ArcGIS MapServer"],
              [ServerType.ArcGISImageServer, "ArcGIS ImageServer"],
              [ServerType.ArcGISFeatureServer, "ArcGIS FeatureServer"],
              //[ServerType.ArcGISOnline, "ArcGIS Online Map"],
              [ServerType.OGCWMS, "OGC WMS"],
              [ServerType.OGCWMTS, "OGC WMTS"],
              [ServerType.OGCWFS, "OGC WFS"],
              [ServerType.TileServer, t('websources.generic_tile_server')]
            ]}
          />
        </Grid>

        {/** Name */}
        <Grid item xs={12}>
          <InputWithLabel
            title={t('websources.server_name')}
            value={serverName}
            label={t('websources.the_name_that_will_be_used_to_identify')}
            placeholder={t('websources.custom_web_source')}
            disabled={submitting}
            onChange={setServerName}
          />
        </Grid>

        {/** URL */}
        <Grid item xs={12}>
          <InputWithLabel
            multiline
            title={t('websources.server_url')}
            value={serverURL}
            disabled={submitting}
            error={error}
            placeholder={placeholder}
            label={label}
            onChange={setServerURL}
          />
        </Grid>

        {showEsriWarning && (<Typography variant="caption" style={{
          padding: theme.spacing(0, 1),
          color: theme.palette.warning.main
        }}>
          {t('websources.all_arcgis_maps_must_be_public')}
        </Typography>)}

      </Grid>
    </SimpleDialog>
  );
};

interface WebSourceLayerCheckboxProps {
  layerID: string;
  visible: boolean;
}

const WebSourceLayerCheckbox = (props: WebSourceLayerCheckboxProps) => {
  const {layerID, visible} = props;

  const dispatch = useDispatch();

  const onVisibilityChange = (visible: boolean) => {
    dispatch(updateWebMapLayer({
      layerID,
      visible
    }));
  };

  return (
    <Checkbox
      size="small"
      checked={visible}
      onChange={(event) => {
        const checked = event.target.checked;
        onVisibilityChange(checked);
      }}
    />
  );
};

interface WebSourceLayerDialogProps {
  open: boolean;
  onClose(): void;
  asset: Asset;
}

export const WebSourceLayerDialog = (props: WebSourceLayerDialogProps) => {
  const {open, onClose, asset} = props;

  const dispatch = useDispatch();
  const {t} = useTranslation();

  const tableState = useTableState({
    defaultSort: "name",
    rowPropertyMap: {
      visible_checkbox: "visible_sortable"
    }
  });

  const layers = useMemo(() => {
    return (asset.data as WebMapData).layers;
  }, [asset.data]);

  const getRows = (layers: WebMapLayerData[]) => {
    return layers.map(layer => {
      return {
        id: `WebSourceLayerTableRow_${layer.id}`,
        name: layer.name,
        visible_sortable: layer.visible ? 1 : 0,
        visible_checkbox: <WebSourceLayerCheckbox
          layerID={layer.id}
          visible={layer.visible}
        />
      };
    });
  };

  const rows = useMemo(() => {
    return getRows(layers);
  }, [layers]);

  const columns = [
    {
      id: 'name',
      label: t('websources.layer_name'),
      wordBreak: true,
      searchable: true
    },
    {
      id: 'visible_checkbox',
      label: <EnhancedTableCheckboxHead
        label={t('websources.visibility')}
        values={layers.map(x => x.visible)}
        onChange={(checked) => {
          layers.forEach(layer => {
            dispatch(updateWebMapLayer({
              layerID: layer.id,
              visible: checked
            }));
          });
        }}
      />,
      center: true
    }
  ];

  return (
    <React.Fragment>
      <DraggableDialog
        open={open}
        title={t('websources.layer_manager')}
        onClose={onClose}
        initialWidth={"fit-content"}
      >
        <DialogContent style={{paddingBottom: "0px", flex: "none"}}>
          <Typography variant="caption">
            <b>{t('asset.file-name')}: </b> {asset.name}
          </Typography>
        </DialogContent>

        <DialogContent>
          <EnhancedTable
            state={tableState}
            columns={columns}
            rows={rows}
          />
        </DialogContent>
      </DraggableDialog>
    </React.Fragment>
  );
};
